import { normalize, schema } from 'normalizr';
import moment from 'moment-timezone';

// Define entities
export const connectionEntity = new schema.Entity(
  'connections',
  {},
  {
    idAttribute: (value) => value.ConversationsConnectionId || value.conversationsConnectionId,
    processStrategy: (value) => ({
      conversationsConnectionId: value.ConversationsConnectionId,
      wabaId: value.WABAId,
      businessAccountId: value.BusinessAccountId,
      phoneNumberId: value.PhoneNumberId,
      phoneNumber: value.PhoneNumber,
      displayName: value.DisplayName,
      accessToken: value.AccessToken,
      webhookVerifyToken: value.WebhookVerifyToken,
      connectedAt: value.ConnectedAt,
      status: value.Status
    })
  }
);

export const contactEntity = new schema.Entity(
  'contacts',
  {},
  { idAttribute: 'conversationsContactId' }
);

export const conversationEntity = new schema.Entity(
  'conversations',
  {},
  {
    idAttribute: (value) => value.ConversationId || value.conversationId,
    processStrategy: (value) => ({
      conversationId: value.ConversationId,
      conversationsConnectionId: value.ConversationsConnectionId,
      recipientType: value.RecipientType,
      recipientId: value.RecipientId,
      conversationsContactId: value.ConversationsContactId,
      groupName: value.GroupName,
      groupDescription: value.GroupDescription,
      unreadCount: value.UnreadCount,
      hasActiveAlert: value.HasActiveAlert,
      lastActivityAt: value.LastActivityAt,
      lastMessagePreview: value.LastMessagePreview,
      lastMessageType: value.LastMessageType,
      lastSenderPhoneNumber: value.LastSenderPhoneNumber,
      displayName: value.DisplayName,
      contactPhoneNumber: value.ContactPhoneNumber,
      firstName: value.FirstName,
      lastName: value.LastName,
      email: value.Email,
      companyName: value.CompanyName,
      profilePictureUrl: value.ProfilePictureUrl,
      notes: value.Notes
    })
  }
);

export const inboundMessageEntity = new schema.Entity(
  'inboundMessages',
  {},
  { idAttribute: 'conversationsInboundMessageId' }
);

export const outboundMessageEntity = new schema.Entity(
  'outboundMessages',
  {},
  { idAttribute: 'conversationsOutboundMessageId' }
);

export const chatAlertEntity = new schema.Entity(
  'chatAlerts',
  {},
  { idAttribute: 'conversationsChatAlertId' }
);

export const callLogEntity = new schema.Entity(
  'callLogs',
  {},
  { idAttribute: 'conversationsCallLogId' }
);

export const callChatEventEntity = new schema.Entity(
  'callChatEvents',
  {},
  { idAttribute: 'conversationsCallChatEventId' }
);

export const groupMemberEntity = new schema.Entity(
  'groupMembers',
  {},
  { idAttribute: 'conversationsGroupMemberId' }
);

// Define schemas
export const connectionListSchema = [connectionEntity];
export const contactListSchema = [contactEntity];
export const conversationListSchema = [conversationEntity];
export const inboundMessageListSchema = [inboundMessageEntity];
export const outboundMessageListSchema = [outboundMessageEntity];

// Normalización helpers
export const normalizeConnections = (data: any[]) => {
  const normalized = normalize(data, connectionListSchema);
  return {
    byId: normalized.entities.connections || {},
    allIds: normalized.result || []
  };
};

export const normalizeContacts = (data: any[]) => {
  const normalized = normalize(data, contactListSchema);
  return {
    byId: normalized.entities.contacts || {},
    allIds: normalized.result || []
  };
};

export const normalizeSingleContact = (data: any) => {
  const normalized = normalize(data, contactEntity);
  return normalized.entities.contacts?.[normalized.result] || data;
};

export const normalizeConversations = (data: any[]) => {
  const normalized = normalize(data, conversationListSchema);
  return {
    byId: normalized.entities.conversations || {},
    allIds: normalized.result || []
  };
};

export const normalizeSingleConversation = (data: any) => {
  const normalized = normalize(data, conversationEntity);
  return normalized.entities.conversations?.[normalized.result] || data;
};

export const normalizeTimeline = (data: {
  conversation: any[];
  timeline: any[];
  groupMembers: any[];
}) => {
  const messages: any[] = [];
  const alerts: any[] = [];
  const callEvents: any[] = [];

  // Separar timeline por tipo usando ItemType
  data.timeline.forEach((item) => {
    if (item.ItemType === 'InboundMessage' || item.ItemType === 'OutboundMessage') {
      messages.push({
        id: `${item.ItemType}-${item.ItemId}`,
        content: item.MessageContent || '',
        timestamp: item.EventDateTime ? moment.utc(item.EventDateTime).toDate() : new Date(),
        type: item.MessageDirection === 'inbound' ? 'inbound' : 'outbound',
        isRead: item.IsRead || false,
        status: item.MessageStatus || 'sent',
        mediaUrl: item.MediaUrl || undefined,
        mediaType: item.MediaType || undefined,
        mediaFileName: item.MediaFileName || undefined,
        mediaFileTitle: item.MediaFileTitle || undefined,
        senderName: item.SenderName || undefined,
        sentByUserId: item.SentByUserId || undefined,
        conversationsOutboundMessageId:
          item.ItemType === 'OutboundMessage' ? item.ItemId : undefined,
        conversationsInboundMessageId: item.ItemType === 'InboundMessage' ? item.ItemId : undefined
      });
    } else if (item.ItemType === 'ChatAlert') {
      alerts.push({
        id: `ChatAlert-${item.ItemId}`,
        type: item.AlertType || 'info',
        title: item.AlertTitle || '',
        description: item.AlertDescription || '',
        timestamp: item.EventDateTime ? moment.utc(item.EventDateTime).toDate() : new Date(),
        eventMetadata: item.EventMetadata || undefined
      });
    } else if (item.ItemType === 'CallChatEvent') {
      // Solo procesar CallChatEvent, ignorar CallLog para evitar duplicados
      callEvents.push({
        id: `${item.ItemType}-${item.ItemId}`,
        status: item.EventType || 'unknown',
        scheduledFor: item.ScheduledFor ? moment.utc(item.ScheduledFor).toDate() : undefined,
        duration: item.CallDuration || undefined,
        timestamp: item.EventDateTime ? moment.utc(item.EventDateTime).toDate() : new Date(),
        recordingUrl: item.RecordingUrl || undefined,
        summary: item.DisplayMessage || undefined,
        transcriptText: item.TranscriptText || undefined
      });
    }
  });

  return {
    conversation: data.conversation[0] || null,
    messages: messages.reverse(),
    alerts: alerts.reverse(),
    callEvents: callEvents.reverse(),
    groupMembers: data.groupMembers || []
  };
};
